import React, { useEffect, useState, useMemo, useRef, ReactNode } from "react";
import { connect } from "react-redux";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import { withRouter } from "tools/routerCompat";
import { RouteComponentProps } from "tools/routerCompat.d";
import MSplitter from "components/Shared/MSplitter";
import MGrid, { ACTION_ADD } from "components/Shared/MGrid";
import MSelect, { MSelectOption } from "components/Shared/MSelect.tsx";
import { CustomerListModel, SurveyTypeListDto } from "interfaces/Mueller.Business.Models";
import {
  searchCustomersText,
  setSelectedSurveyType,
  getCustomerDetails,
  setSelectedCustomerNumber,
} from "actions/customer";
import { getSurveyTypesByCustomer } from "requests/customer";
import { getCustomerForms } from "actions/customerConfiguration";
import { DATA_TYPE_BOOLEAN, DATA_TYPE_STRING, DATA_TYPE_DATE } from "typeConstants/dataTypes";
import CopySurveyTypeWizard from "./CopySurveyTypeWizard/CopySurveyTypeWizard";
import { MDialog } from "components/Shared";
import responseHandler from "responseHandler";

interface CustomerSurveyTypesProps extends RouteComponentProps {
  splitComponent: ReactNode;
  splitComponentTitle: string;
  // Redux
  getCustomers: () => void;
  fetchingCustomerList: boolean;
  customerList: CustomerListModel[];
  setSelectedSurveyType: (surveyType: SurveyTypeListDto) => void;
  selectedSurveyType: SurveyTypeListDto;
  getCustomerDetails: (customerNumber: number) => void;
  setSelectedCustomerNumber: (customerNumber: string | number) => void;
  selectedCustomerNumber: number;
  getCustomerForms: (customerNumber: number) => void;
  fetchingFormOptions: boolean;
  makeSuccessToast: (text: string) => void;
}

const CustomerSurveyTypes = (props: CustomerSurveyTypesProps) => {
  const [surveyTypes, setSurveyTypes] = useState<SurveyTypeListDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const mounted = useRef(true);
  const openedFullScreen = useRef(false);

  useEffect(() => {
    // The customer list we're using in redux could be customer search results, so we should always get customers even if there are some in redux
    if (!props.fetchingCustomerList) {
      props.getCustomers();
    }
    return () => {
      mounted.current = false;
      // If not opening full screen, clear selections so another component can navigate here with its own settings
      if (!openedFullScreen.current) {
        props.setSelectedCustomerNumber(null);
        props.setSelectedSurveyType(null);
      }
    };
  }, []);

  useEffect(() => {
    if (props.selectedCustomerNumber) {
      setIsLoading(true);
      props.setSelectedSurveyType(null);
      props.getCustomerDetails(props.selectedCustomerNumber);
      // Used by CustomerSurveyTypeForms component. Fetched here when customer changes
      if (!props.fetchingFormOptions) {
        props.getCustomerForms(props.selectedCustomerNumber);
      }
      const handleSurveyTypes = (response: SurveyTypeListDto[]) => {
        setSurveyTypes(response);
        setIsLoading(false);
      };
      getSurveyTypesByCustomer(props.selectedCustomerNumber, handleSurveyTypes, mounted);
    }
  }, [props.selectedCustomerNumber]);

  // Customer Dropdown

  const customerOptions = useMemo(() => {
    let result = [...props.customerList].sort((a, b) => a.number - b.number);
    return result.map((c) => ({
      label: `${c.number} ${c.displayName}${c.inactiveDate ? " (Inactive)" : ""}`,
      value: c.number,
    }));
  }, [props.customerList]);

  const onCustomerChange = (option: MSelectOption) => {
    props.setSelectedCustomerNumber(option?.value);
  };

  const selectedOption = props.selectedCustomerNumber
    ? customerOptions.find((o) => o.value === props.selectedCustomerNumber)
    : null;
  const getCustomerPortalTarget = () => document.querySelector("customer-dropdown");

  // Grid

  const columns = [
    { key: "name", name: "Name", dataSource: "name", width: 450, dataType: DATA_TYPE_STRING },
    { key: "code", name: "Code", dataSource: "code", width: 200, dataType: DATA_TYPE_STRING },
    { key: "inactiveDate", name: "Inactive Date", dataSource: "inactiveDate", width: 100, dataType: DATA_TYPE_DATE },
    {
      key: "hasBillingRules",
      name: "Billing Rules",
      dataSource: "hasBillingRules",
      width: 100,
      dataType: DATA_TYPE_BOOLEAN,
    },
  ];

  const onRowClick = (index: number) => props.setSelectedSurveyType(surveyTypes[index]);
  const clearSelectedSurveyType = () => props.setSelectedSurveyType(null);
  const onAddSurveyType = () => {
    setShowAddDialog(true);
  };

  // Add Survey Type Dialog
  // This dialog uses MDialog due to some child components using DialogContext

  const hideAddDialog = () => setShowAddDialog(false);
  const onSubmit = (newSurveyTypeCode: string) => {
    setIsLoading(true);
    hideAddDialog();
    const handleSurveyTypes = (response: SurveyTypeListDto[]) => {
      setSurveyTypes(response);
      const newSurveyType = response.find((s) => s.code === newSurveyTypeCode);
      if (newSurveyType) {
        props.makeSuccessToast("Survey Type Created. Please review to confirm all settings are accurate.");
        setIsLoading(false);
        props.setSelectedSurveyType(newSurveyType);
      }
    };
    getSurveyTypesByCustomer(props.selectedCustomerNumber, handleSurveyTypes, mounted);
  };

  // MSplitter

  const onFullScreenClick = () => {
    props.history.push({ pathname: "/customer/survey-type" });
    openedFullScreen.current = true;
  };

  // Render

  return (
    <MSplitter
      firstChild={
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ p: 1, flexShrink: 0, "& .customer-dropdown": { maxWidth: 400 } }}>
              <p>Select Customer Account:</p>
              <MSelect
                className="customer-dropdown"
                key="customer-dropdown"
                label="Select Customer Account"
                placeholder="Select Account..."
                isClearable={false}
                onChange={onCustomerChange}
                options={customerOptions}
                value={selectedOption}
                getMenuPortalTarget={getCustomerPortalTarget}
              />
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <MGrid
                configurationId="CustomerSurveyTypeList"
                headerText="Survey Types"
                columnDefinitions={columns}
                data={surveyTypes}
                isFetchingData={props.fetchingCustomerList || isLoading}
                onRowClick={onRowClick}
                displayNumOfItemsFound={true}
                actions={selectedOption ? [{ code: ACTION_ADD, onClick: onAddSurveyType }] : null}
              />
            </Box>
          </Card>
          <MDialog
            id="add-survey-type-wizard"
            title="Add New Survey Type"
            className="add-survey-type-dialog"
            visible={showAddDialog}
            dontSpecifyWidth
          >
            <CopySurveyTypeWizard onSubmit={onSubmit} onCancel={hideAddDialog} />
          </MDialog>
        </Box>
      }
      secondChild={props.selectedSurveyType && props.splitComponent}
      secondChildTitle={{
        title: props.splitComponentTitle,
        buttons: [
          {
            icon: "fullscreen",
            onClick: onFullScreenClick,
          },
          { icon: "close", onClick: clearSelectedSurveyType },
        ],
      }}
    />
  );
};

const mapStateToProps = (state) => ({
  customerList: state.customer.customerList,
  fetchingCustomerList: state.customer.fetchingCustomerList,
  selectedSurveyType: state.customer.selectedSurveyType,
  selectedCustomerNumber: state.customer.selectedCustomerNumber,
  fetchingFormOptions: state.customer.fetchingFormOptions,
});

const mapDispatchToProps = (dispatch) => ({
  getCustomers() {
    dispatch(searchCustomersText()); // Searching without a search term will get all customers
  },
  setSelectedSurveyType(surveyType: SurveyTypeListDto) {
    dispatch(setSelectedSurveyType(surveyType));
  },
  getCustomerDetails(customerNumber: number) {
    dispatch(getCustomerDetails(customerNumber));
  },
  setSelectedCustomerNumber(customerNumber: string | number) {
    dispatch(setSelectedCustomerNumber(customerNumber));
  },
  getCustomerForms(customerNumber: number) {
    dispatch(getCustomerForms(customerNumber));
  },
  makeSuccessToast(text) {
    responseHandler.customToast(text, "success", dispatch);
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CustomerSurveyTypes));
